/* eslint-disable no-unused-vars */
'use strict';

/* ================================================================
   Gift Card PDP — ma-gift-card-form custom element
   Handles: mode toggle · live preview · date picker · validation
   ================================================================ */

class MaGiftCardForm extends HTMLElement {
  connectedCallback() {
    this._sid = this.dataset.sectionId;
    this._mode = 'gift';
    this._deliveryMode = 'now';
    this._pendingDate = null;
    this._selectedDate = null;
    this._dpMonth = null;
    this._submitAttempted = false;
    this._variantPrice = parseInt(this.dataset.variantPrice || '0', 10);

    this._initTimezone();
    this._initModeToggle();
    this._initFieldListeners();
    this._initDeliveryToggle();
    this._initDatePicker();
    this._initFormSubmit();
    this._initVariantListener();
    this._updatePreview();
    this._updateATCButton();
  }

  /* ── Timezone ── */
  _initTimezone() {
    var tz = this.querySelector('[data-gc-tz-offset]');
    if (tz) tz.value = new Date().getTimezoneOffset();
  }

  /* ── Mode toggle ── */
  _initModeToggle() {
    var self = this;
    var cards = this.querySelectorAll('[data-gc-mode-card]');
    cards.forEach(function(card) {
      card.addEventListener('click', function() {
        var radio = card.querySelector('input[type="radio"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      var radio = card.querySelector('input[type="radio"]');
      if (radio) {
        radio.addEventListener('change', function() {
          self._mode = radio.value;
          self._updateMode();
          self._maybeClearSummary();
          self._updatePreview();
          self._updateATCButton();
        });
      }
    });
  }

  _updateMode() {
    var mode = this._mode;
    var cards = this.querySelectorAll('[data-gc-mode-card]');
    cards.forEach(function(c) {
      c.classList.toggle('gc-mode-card--active', c.dataset.gcModeCard === mode);
    });

    var recipientPanel = this._el('gc-recipient-panel');
    var selfPanel = this._el('gc-self-panel');
    var control = this._el('gc-control');

    if (mode === 'gift') {
      if (recipientPanel) recipientPanel.hidden = false;
      if (selfPanel) selfPanel.hidden = true;
      if (control) control.disabled = false;
      this._setFieldsDisabled(false);
    } else {
      if (recipientPanel) recipientPanel.hidden = true;
      if (selfPanel) selfPanel.hidden = false;
      if (control) control.disabled = true;
      this._setFieldsDisabled(true);
    }

    var lr = this._el('gc-live-region');
    if (lr) {
      lr.textContent = mode === 'gift'
        ? 'Gift mode selected. Recipient form expanded.'
        : 'Personal use selected. Recipient form hidden.';
    }
  }

  _setFieldsDisabled(disabled) {
    var self = this;
    ['gc-email', 'gc-name', 'gc-message', 'gc-send-on', 'gc-tz-offset'].forEach(function(key) {
      var el = self._el(key);
      if (el) el.disabled = disabled;
    });
  }

  /* ── Field listeners (live preview + char counter) ── */
  _initFieldListeners() {
    var self = this;

    var email = this._el('gc-email');
    if (email) {
      email.addEventListener('input', function() {
        self._setInputState('email', email.value, true);
        if (self._submitAttempted) self._clearFieldError('email');
        self._maybeClearSummary();
      });
      email.addEventListener('blur', function() {
        if (self._submitAttempted && !self._isValidEmail(email.value.trim())) {
          self._markFieldError('email', 'Enter a valid email address.');
        }
      });
    }

    var name = this._el('gc-name');
    if (name) {
      name.addEventListener('input', function() {
        self._setInputState('name', name.value, true);
        self._updatePreview();
      });
    }

    var msg = this._el('gc-message');
    if (msg) {
      msg.addEventListener('input', function() {
        var len = msg.value.length;
        var counter = self._el('gc-char-counter');
        if (counter) {
          counter.textContent = len + ' / 200';
          counter.classList.toggle('gc-char-counter--over', len > 200);
        }
        var wrap = self.querySelector('[data-gc-textarea-wrap="message"]');
        if (wrap) wrap.classList.toggle('gc-textarea-wrap--filled', len > 0);
        self._updatePreview();
      });
    }
  }

  _setInputState(field, value, showOk) {
    var wrap = this.querySelector('[data-gc-input-wrap="' + field + '"]');
    if (!wrap) return;
    var filled = value.trim().length > 0;
    wrap.classList.toggle('gc-input-wrap--filled', filled && showOk);
    wrap.classList.remove('gc-input-wrap--error');
  }

  _markFieldError(field, msg) {
    var wrap = this.querySelector('[data-gc-input-wrap="' + field + '"]');
    if (wrap) {
      wrap.classList.add('gc-input-wrap--error');
      wrap.classList.remove('gc-input-wrap--filled');
    }
    var errEl = this._el('gc-' + field + '-error');
    if (errEl) {
      errEl.hidden = false;
      var msgEl = errEl.querySelector('.gc-field-error__msg');
      if (msgEl) msgEl.textContent = msg;
    }
  }

  _clearFieldError(field) {
    var wrap = this.querySelector('[data-gc-input-wrap="' + field + '"]');
    if (wrap) wrap.classList.remove('gc-input-wrap--error');
    var errEl = this._el('gc-' + field + '-error');
    if (errEl) errEl.hidden = true;
  }

  _maybeClearSummary() {
    if (!this._submitAttempted) return;
    if (this._validate().length === 0) {
      var summaryEl = this._el('gc-error-summary');
      if (summaryEl) summaryEl.hidden = true;
    }
  }

  /* ── Delivery toggle ── */
  _initDeliveryToggle() {
    var self = this;
    var tabs = this.querySelectorAll('[data-gc-delivery]');
    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        var mode = tab.dataset.gcDelivery;
        self._deliveryMode = mode;
        tabs.forEach(function(t) {
          t.classList.toggle('gc-delivery-tab--active', t.dataset.gcDelivery === mode);
        });
        var dateField = self._el('gc-date-field');
        if (dateField) dateField.hidden = mode !== 'later';

        if (mode !== 'later') {
          var sendOn = self._el('gc-send-on');
          if (sendOn) sendOn.value = '';
          self._selectedDate = null;
          self._pendingDate = null;
          var dateErr = self._el('gc-date-error');
          if (dateErr) dateErr.hidden = true;
        }
        self._maybeClearSummary();
        self._updatePreview();
        self._updateATCButton();
      });
    });
  }

  /* ── Date picker ── */
  _initDatePicker() {
    var self = this;
    var trigger = this._el('gc-date-trigger');
    var picker = this._el('gc-datepicker');
    var cancelBtn = this._el('gc-dp-cancel');
    var commitBtn = this._el('gc-dp-commit');
    var prevBtn = this._el('gc-dp-prev');
    var nextBtn = this._el('gc-dp-next');

    if (!trigger || !picker) return;

    /* Portal: move picker to <body> so Dawn's scroll-animation transform on
       .product__info-wrapper doesn't hijack the position:fixed coordinate space.
       The element keeps its ID so _el() lookups still work. */
    document.body.appendChild(picker);

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    this._dpMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this._renderCalendar();

    trigger.addEventListener('click', function() {
      var isOpen = !picker.hidden;
      if (isOpen) {
        picker.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        picker.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        self._pendingDate = self._selectedDate;
        self._renderCalendar();
        self._positionPicker(trigger, picker);
      }
    });

    cancelBtn.addEventListener('click', function() {
      picker.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      self._pendingDate = self._selectedDate;
    });

    commitBtn.addEventListener('click', function() {
      if (!self._pendingDate) return;
      self._selectedDate = new Date(self._pendingDate);
      picker.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');

      var sendOn = self._el('gc-send-on');
      if (sendOn) {
        var d = self._selectedDate;
        sendOn.value = d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
      }

      var label = self._el('gc-date-label');
      if (label) label.textContent = self._fmtHuman(self._selectedDate);

      var dateErr = self._el('gc-date-error');
      if (dateErr) dateErr.hidden = true;

      self._maybeClearSummary();
      self._updatePreview();
      self._updateATCButton();
    });

    prevBtn.addEventListener('click', function() {
      self._dpMonth = new Date(self._dpMonth.getFullYear(), self._dpMonth.getMonth() - 1, 1);
      self._renderCalendar();
    });

    nextBtn.addEventListener('click', function() {
      self._dpMonth = new Date(self._dpMonth.getFullYear(), self._dpMonth.getMonth() + 1, 1);
      self._renderCalendar();
    });

    document.addEventListener('click', function(e) {
      if (picker.hidden) return;
      /* picker clicks are stopped by stopPropagation below; trigger click
         is handled by its own listener — only close for clicks elsewhere */
      if (!trigger.contains(e.target)) {
        picker.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    /* Prevent picker clicks from bubbling to the document close-handler.
       Without this, _renderCalendar() detaches the clicked day button before
       the event reaches document, causing self.contains(e.target) → false
       and the picker closes immediately after a date is selected. */
    picker.addEventListener('click', function(e) {
      e.stopPropagation();
    });

    picker.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        picker.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    });
  }

  _renderCalendar() {
    var monthEl = this._el('gc-dp-month');
    var presetsEl = this._el('gc-dp-presets');
    var gridEl = this._el('gc-dp-grid');
    var self = this;
    if (!monthEl || !gridEl) return;

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 365);

    var MONTHS = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
    monthEl.textContent = MONTHS[this._dpMonth.getMonth()] + ' ' + this._dpMonth.getFullYear();

    /* Presets */
    var presets = this._buildPresets(today);
    presetsEl.innerHTML = '';
    presets.forEach(function(p) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gc-datepicker__preset' +
        (self._pendingDate && self._sameDay(p.date, self._pendingDate) ? ' gc-datepicker__preset--active' : '');
      btn.innerHTML =
        '<div class="gc-datepicker__preset-title">' + self._esc(p.label) + '</div>' +
        '<div class="gc-datepicker__preset-sub">' + self._esc(p.sub) + '</div>';
      btn.addEventListener('click', function() {
        self._pendingDate = new Date(p.date);
        self._dpMonth = new Date(p.date.getFullYear(), p.date.getMonth(), 1);
        self._renderCalendar();
        self._updateCommitFooter();
      });
      presetsEl.appendChild(btn);
    });

    /* Day grid */
    var firstDay = new Date(this._dpMonth.getFullYear(), this._dpMonth.getMonth(), 1);
    var lastDay = new Date(this._dpMonth.getFullYear(), this._dpMonth.getMonth() + 1, 0);
    var cells = [];

    for (var i = 0; i < firstDay.getDay(); i++) {
      var d = new Date(firstDay);
      d.setDate(d.getDate() - (firstDay.getDay() - i));
      cells.push({ date: d, other: true });
    }
    for (var d2 = 1; d2 <= lastDay.getDate(); d2++) {
      cells.push({ date: new Date(this._dpMonth.getFullYear(), this._dpMonth.getMonth(), d2), other: false });
    }
    while (cells.length < 42) {
      var last = cells[cells.length - 1].date;
      var next = new Date(last);
      next.setDate(next.getDate() + 1);
      cells.push({ date: next, other: true });
    }

    gridEl.innerHTML = '';
    var rowEl = null;
    cells.forEach(function(cell, idx) {
      if (idx % 7 === 0) {
        rowEl = document.createElement('div');
        rowEl.className = 'gc-datepicker__week';
        gridEl.appendChild(rowEl);
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = cell.date.getDate();
      btn.setAttribute('aria-label', self._fmtHuman(cell.date));

      var isPast = cell.date < today;
      var isFuture = cell.date > maxDate;
      var isDisabled = isPast || isFuture;
      var isToday = self._sameDay(cell.date, today);
      var isSel = self._pendingDate && self._sameDay(cell.date, self._pendingDate);

      var cls = 'gc-datepicker__day';
      if (cell.other) cls += ' gc-datepicker__day--dim';
      if (isDisabled && !isSel) cls += ' gc-datepicker__day--disabled';
      if (isToday && !isSel) cls += ' gc-datepicker__day--today';
      if (isSel) cls += ' gc-datepicker__day--selected';
      btn.className = cls;

      if (isDisabled) {
        btn.disabled = true;
      } else {
        btn.addEventListener('click', function() {
          self._pendingDate = new Date(cell.date);
          self._renderCalendar();
          self._updateCommitFooter();
        });
      }
      rowEl.appendChild(btn);
    });

    this._updateCommitFooter();
  }

  _updateCommitFooter() {
    var el = this._el('gc-dp-selected-label');
    var commitBtn = this._el('gc-dp-commit');
    if (el) el.textContent = this._pendingDate ? this._fmtHuman(this._pendingDate) : 'Pick a date';
    if (commitBtn) commitBtn.disabled = !this._pendingDate;
  }

  _buildPresets(today) {
    var tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return [
      { label: 'Send today',  sub: 'In a minute or two', date: new Date(today) },
      { label: 'Tomorrow',    sub: DAYS[tomorrow.getDay()] + ' · ' + MONS[tomorrow.getMonth()] + ' ' + tomorrow.getDate(), date: new Date(tomorrow) },
    ];
  }

  /* ── Validation + submit ── */
  _initFormSubmit() {
    var self = this;
    var form = this.closest('form');
    if (!form) return;
    form.addEventListener('submit', function(e) {
      if (self._mode !== 'gift') return;
      self._submitAttempted = true;
      var errors = self._validate();
      if (errors.length > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self._showErrors(errors);
      }
    }, true);
  }

  _validate() {
    if (this._mode !== 'gift') return [];
    var errors = [];
    var email = this._el('gc-email');
    var val = email ? email.value.trim() : '';
    if (!val) {
      errors.push({ field: 'email', msg: 'Recipient email is required.' });
    } else if (!this._isValidEmail(val)) {
      errors.push({ field: 'email', msg: "That doesn’t look like a valid email — double-check the format." });
    }
    if (this._deliveryMode === 'later') {
      var sendOn = this._el('gc-send-on');
      if (!sendOn || !sendOn.value) {
        errors.push({ field: 'date', msg: 'Pick a date in the future, or switch back to “Send right away”.' });
      }
    }
    return errors;
  }

  _showErrors(errors) {
    var self = this;
    var summaryEl = this._el('gc-error-summary');
    var titleEl = this._el('gc-error-summary-title');
    var listEl = this._el('gc-error-summary-list');
    var lr = this._el('gc-live-region');

    if (summaryEl) {
      summaryEl.hidden = false;
      if (titleEl) titleEl.textContent = errors.length === 1 ? 'There’s 1 thing to fix:' : 'There are ' + errors.length + ' things to fix:';
      if (listEl) {
        listEl.innerHTML = errors.map(function(e) {
          var id = e.field === 'email' ? 'gc-email-' + self._sid : 'gc-date-trigger-' + self._sid;
          return '<li><a href="#' + id + '">' + self._esc(e.msg) + '</a></li>';
        }).join('');
      }
      summaryEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    if (lr) lr.textContent = errors.length === 1 ? 'There is 1 thing to fix.' : 'There are ' + errors.length + ' things to fix.';

    errors.forEach(function(err) {
      if (err.field === 'email') {
        self._markFieldError('email', err.msg);
        var email = self._el('gc-email');
        if (email) setTimeout(function() { email.focus(); }, 100);
      } else if (err.field === 'date') {
        var errEl = self._el('gc-date-error');
        if (errEl) {
          errEl.hidden = false;
          var msgEl = errEl.querySelector('.gc-field-error__msg');
          if (msgEl) msgEl.textContent = err.msg;
        }
        var trigger = self._el('gc-date-trigger');
        if (trigger && errors[0].field === 'date') setTimeout(function() { trigger.focus(); }, 100);
      }
    });
  }

  /* ── Variant change subscription ── */
  _initVariantListener() {
    var self = this;
    function subscribe_() {
      if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
      subscribe(PUB_SUB_EVENTS.variantChange, function(event) {
        if (!event || !event.data || event.data.sectionId !== self._sid) return;
        var v = event.data.variant;
        if (!v) return;
        self._variantPrice = v.price;
        self._updateAmountDisplay();
        self._updateATCButton();
        self._updatePreview();
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', subscribe_);
    } else {
      subscribe_();
    }
  }

  _updateAmountDisplay() {
    var el = document.getElementById('gc-card-amount-' + this._sid);
    if (el && this._variantPrice) el.textContent = '$' + Math.floor(this._variantPrice / 100);
  }

  _updateATCButton() {
    var btn = document.getElementById('ProductSubmitButton-' + this._sid);
    if (!btn) return;
    var span = btn.querySelector('span:first-child');
    if (!span) return;

    var dollars = this._variantPrice ? '$' + Math.floor(this._variantPrice / 100) : '';
    var scheduled = this._deliveryMode === 'later' && this._selectedDate;
    var verb;

    if (this._mode === 'gift') {
      if (scheduled) {
        verb = '🎁 Schedule ' + dollars + ' gift card →';
      } else {
        verb = '🎁 Send ' + dollars + ' gift card →';
      }
    } else {
      verb = '✨ Add ' + dollars + ' gift card to cart →';
    }

    span.textContent = verb;
  }

  /* ── Live preview update ── */
  _updatePreview() {
    var nameEl = this._el('gc-name');
    var msgEl = this._el('gc-message');
    var name = nameEl ? nameEl.value.trim() : '';
    var msg = msgEl ? msgEl.value.trim() : '';
    var inGift = this._mode === 'gift';
    var scheduled = this._deliveryMode === 'later' && this._selectedDate;

    /* Card name */
    var cardName = document.getElementById('gc-card-name-' + this._sid);
    if (cardName) {
      if (!inGift || !name) {
        cardName.innerHTML = '<span style="opacity:0.55">Their name here</span>';
      } else {
        cardName.textContent = name;
      }
    }

    /* Card middle (message or tagline) */
    var cardMid = document.getElementById('gc-card-mid-' + this._sid);
    if (cardMid) {
      if (inGift && msg) {
        cardMid.innerHTML =
          '<div class="gc-card__msg-label">MESSAGE</div>' +
          '<div class="gc-card__msg-text">“' + this._esc(msg) + '”</div>';
      } else {
        cardMid.innerHTML = '<div class="gc-card__tagline">Snack pass for the people you actually like.</div>';
      }
    }

    /* Scheduled badge */
    var prevBadge = document.getElementById('gc-card-sched-' + this._sid);
    if (prevBadge) prevBadge.remove();
    if (inGift && scheduled) {
      var toEl = cardName && cardName.parentElement;
      if (toEl) {
        var badge = document.createElement('div');
        badge.id = 'gc-card-sched-' + this._sid;
        badge.className = 'gc-card__scheduled-badge';
        badge.textContent = '⏱ ARRIVES ' + this._fmtHuman(this._selectedDate).toUpperCase();
        toEl.appendChild(badge);
      }
    }
  }

  /* ── Helpers ── */
  _positionPicker(trigger, picker) {
    if (window.innerWidth <= 749) {
      /* Mobile: CSS handles bottom-sheet positioning */
      picker.style.top = '';
      picker.style.left = '';
      return;
    }
    var rect = trigger.getBoundingClientRect();
    var scrollY = window.scrollY || window.pageYOffset;
    var scrollX = window.scrollX || window.pageXOffset;
    var pickerW = 360;
    var top = rect.bottom + scrollY + 6;
    var left = rect.left + scrollX;
    if (left + pickerW > window.innerWidth - 8) {
      left = Math.max(8 + scrollX, window.innerWidth - pickerW - 8 + scrollX);
    }
    picker.style.top = top + 'px';
    picker.style.left = left + 'px';
  }

  _el(id) { return document.getElementById(id + '-' + this._sid); }

  _sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
  }

  _fmtHuman(d) {
    var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return DAYS[d.getDay()] + ', ' + MONS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  _isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  _esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('ma-gift-card-form')) {
  customElements.define('ma-gift-card-form', MaGiftCardForm);
}
